import { ArrowRight } from "lucide-react";
import { cx } from "../../lib/cx";
import { imageProps, localHref, photoProps } from "../../lib/links";

const avatarTones = [
  "bg-slate text-white",
  "bg-blue-deep text-white",
  "bg-yellow text-ink",
];

/**
 * A person's photo (`photo`, an asset filename, or `photoUrl`, a local URL),
 * or a letter avatar when there is none. Fills its box;
 * faces sit in the upper part of most team photos, so crops favour the top.
 */
export function PersonPhoto({
  person,
  sizes,
  alt = "",
  eager = false,
  className,
}) {
  const props = person.photo
    ? photoProps(person.photo, sizes)
    : person.photoUrl && imageProps(person.photoUrl, sizes);
  if (props?.src)
    return (
      <img
        {...props}
        alt={alt}
        loading={eager ? undefined : "lazy"}
        fetchPriority={eager ? "high" : undefined}
        className={cx("h-full w-full object-cover object-[50%_28%]", className)}
      />
    );
  const tone = avatarTones[(person.name?.charCodeAt(0) || 0) % 3];
  return (
    <div
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : "true"}
      className={cx(
        "font-display grid h-full w-full place-items-center text-[64px]",
        tone,
        className,
      )}
    >
      {(person.name || "?").charAt(0)}
    </div>
  );
}

/** Roster card: photo, Oswald name, title and a profile link. */
export default function PersonCard({ person, featured = false, className }) {
  return (
    <a
      href={localHref(person.path)}
      className={cx(
        "group flex h-full flex-col overflow-hidden rounded-brand border border-line bg-white transition duration-150 hover:-translate-y-0.5 hover:shadow-soft motion-reduce:hover:translate-y-0",
        className,
      )}
    >
      <div
        className={cx(
          "relative overflow-hidden bg-surface",
          featured ? "aspect-[4/3]" : "aspect-[4/5]",
        )}
      >
        <PersonPhoto
          person={person}
          sizes={
            featured
              ? "(max-width: 640px) 100vw, 380px"
              : "(max-width: 640px) 50vw, 240px"
          }
          className="transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:group-hover:scale-100"
        />
        <span
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-yellow transition-transform duration-300 group-hover:scale-x-100"
        />
      </div>
      <div
        className={cx(
          "flex flex-1 flex-col px-4 pt-4 pb-4 sm:px-5",
          featured && "sm:px-6 sm:pt-5 sm:pb-5",
        )}
      >
        <strong
          className={cx(
            "font-display tracking-[0.03em] text-ink uppercase",
            featured ? "text-[24px]" : "text-[19px]",
          )}
        >
          {person.name}
        </strong>
        <span className="mt-0.5 text-[14px] leading-snug text-muted">
          {person.title}
        </span>
        <span className="tag mt-auto flex items-center gap-1.5 pt-3 text-[11px] font-semibold text-blue-deep">
          View profile
          <ArrowRight
            size={13}
            aria-hidden="true"
            className="transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </div>
    </a>
  );
}
